import { useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import AppContext from '../../context';
import Data from '../../repository';
import Loading from '../components/Loading';

import styles from '../../styles/Tokens.module.scss';

const formatMoney = (amount) => `£${(Number(amount || 0) / 100).toFixed(2)}`;
const formatTokens = (amount) => `${Number(amount || 0)} token${Number(amount || 0) === 1 ? '' : 's'}`;
const formatTokenType = (tokenType) => {
	if (tokenType === 'open_gym') return 'Open gym';
	if (tokenType === 'class') return 'Class';
	return null;
};

const getTransactionLabel = (transaction) => {
	if (transaction.WalletPackage?.name && transaction.type === 'purchase') {
		return transaction.WalletPackage.name;
	}

	if (transaction.ExperienceBooking?.reference) {
		return `${transaction.type === 'refund' ? 'Experience refund' : 'Experience booking'} ${transaction.ExperienceBooking.reference}`;
	}

	if (transaction.ServiceBooking?.reference) {
		return `${transaction.type === 'refund' ? 'Appointment refund' : 'Appointment booking'} ${transaction.ServiceBooking.reference}`;
	}

	if (transaction.note) return transaction.note;

	return transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1);
};

function WalletList({ basePath = '/dashboard/wallet', topUpPath = null }) {
	const context = useContext(AppContext);
	const { fetchUser } = context;
	const [searchParams] = useSearchParams();

	const [loading, setLoading] = useState(true);
	const [packages, setPackages] = useState([]);
	const [transactions, setTransactions] = useState([]);
	const [summary, setSummary] = useState(null);
	const [message, setMessage] = useState(null);
	const [messageVariant, setMessageVariant] = useState('success');

	const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
	const isSumup = context.domain?.paymentProcessor === 'SumUp';
	const isStripe = context.domain?.paymentProcessor === 'Stripe';
	const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
	const isHybridNew = context.domain?.businessType === 'hybrid-new';
	const formatBalance = (amount) => isHybrid ? formatTokens(amount) : formatMoney(amount);

	useEffect(() => {
		const init = async () => {
			try {
				const loadWalletData = async () => {
					const [summaryRes, packagesRes, transactionsRes] = await Promise.all([
						Data.fetchWalletSummary(),
						Data.fetchPublicWalletPackages(),
						Data.fetchWalletTransactions()
					]);

					setSummary(summaryRes.data);
					setPackages(packagesRes.data || []);
					setTransactions(transactionsRes.data || []);
				};

				await loadWalletData();

				const paymentStatus = searchParams.get('paymentStatus');
				const transactionId = searchParams.get('transactionId');
				const checkoutId = searchParams.get('checkoutId');

				if (paymentStatus === 'success' && transactionId) {
					if (isSumup) {
						try {
							await Data.verifyWalletSumupPayment(transactionId, checkoutId);
							setMessage('Wallet top-up verified and your balance has been updated.');
							setMessageVariant('success');
						} catch (error) {
							setMessage('Payment verification is still pending. Refresh in a moment if your balance does not update.');
							setMessageVariant('notSuccess');
						}
					} else if (isWorldpay) {
						try {
							await Data.verifyWalletWorldpayPayment(transactionId);
							setMessage('Wallet top-up verified and your balance has been updated.');
							setMessageVariant('success');
						} catch (error) {
							setMessage('Payment verification is still pending. Refresh in a moment if your balance does not update.');
							setMessageVariant('notSuccess');
						}
					} else if (isStripe) {
						setMessage('Payment received. Your wallet balance will update shortly.');
						setMessageVariant('success');
					}

					await loadWalletData();
					await fetchUser();
				} else if (paymentStatus === 'failed') {
					setMessage('Your wallet payment did not complete, please try again.');
					setMessageVariant('notSuccess');
				} else if (paymentStatus === 'pending') {
					setMessage('Your wallet payment is pending, please check back soon.');
					setMessageVariant('notSuccess');
				}
			} catch (error) {
				setMessage('Unable to load wallet details right now.');
				setMessageVariant('error');
			} finally {
				setLoading(false);
			}
		};

		init();
	}, [fetchUser, isStripe, isSumup, isWorldpay, searchParams]);

	const currentTopUpPath = topUpPath || basePath;
	const lowBalance = !!summary?.lowBalance;

	if (loading) return <Loading button={false} />;

	return (
		<>
			{message ? (
				<div className={styles.container}>
					<p className={styles[messageVariant]}>{message}</p>
				</div>
			) : null}

			<section className={styles.balance}>
				<div>
					<h2>Wallet Balance</h2>
					<p>{formatBalance(summary?.balance ?? context.user?.walletBalance)}</p>
				</div>
				{isHybridNew ? (
					<div className={styles.balanceMeta}>
						<p>Open gym: {formatTokens(summary?.openGymBalance ?? context.user?.openGymBalance)}</p>
						<p>Classes: {formatTokens(summary?.classBalance ?? context.user?.classBalance)}</p>
					</div>
				) : null}
				<div className={styles.balanceMeta}>
					<p>Low balance threshold: {formatBalance(summary?.lowBalanceThreshold ?? context.domain?.walletLowBalanceThreshold)}</p>
					{lowBalance ? <p className={styles.lowBalanceText}>Your balance is running low.</p> : null}
				</div>
			</section>

			{lowBalance ? (
				<section>
					<p className={styles.notSuccess}>
						Your wallet is below the reminder threshold. <Link to={currentTopUpPath}>Top up now</Link>.
					</p>
				</section>
			) : null}

			<section>
				<h2>Wallet Packages</h2>
				<div className={styles.products}>
					{!packages.length ? (
						<div className={styles.none}>
							<p>There aren't any wallet packages available for purchase right now.</p>
						</div>
					) : (
						packages.map((walletPackage) => (
							<div key={walletPackage._id} className={styles.product}>
								<div>
									<p><strong>{walletPackage.name}</strong></p>
									<p className={styles.packageMeta}>
										Pay {formatMoney(walletPackage.purchaseAmount)} and receive {formatBalance(walletPackage.creditAmount)}.
									</p>
									{walletPackage.description ? <p>{walletPackage.description}</p> : null}
								</div>
								<Link to={`${basePath}/checkout/${walletPackage._id}`} className="btn btnPrimary">
									Buy for {formatMoney(walletPackage.purchaseAmount)}
								</Link>
							</div>
						))
					)}
				</div>
			</section>

			<section>
				<h2>Wallet Activity</h2>
				<div className={styles.transactions}>
					{!transactions.length ? (
						<div className={styles.none}>
							<p>No wallet activity yet.</p>
						</div>
					) : (
						transactions.map((transaction) => (
							<div key={transaction._id} className={styles.transactionRow}>
								<div>
									<p><strong>{getTransactionLabel(transaction)}</strong></p>
									<p>{moment(transaction.createdAt).format('Do MMM YYYY, HH:mm')}</p>
									{isHybridNew && formatTokenType(transaction.tokenType) ? <p>{formatTokenType(transaction.tokenType)} tokens</p> : null}
								</div>
								<p className={transaction.amount >= 0 ? styles.creditAmount : styles.debitAmount}>
									{transaction.amount >= 0 ? '+' : '-'}{formatBalance(Math.abs(transaction.amount))}
								</p>
							</div>
						))
					)}
				</div>
			</section>
		</>
	);
}

export default WalletList;
